/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * The one place a frontend failure becomes visible.
 *
 * Until now a failed request either vanished into a `.catch(() => empty)` or
 * reached the console only if some caller happened to log it, so "broken" and
 * "genuinely empty" were indistinguishable — on the screen and in support
 * calls alike. Every error path should end up here, whatever the caller then
 * shows the user.
 *
 * Two sinks, both deliberate:
 *  - `console.error`, structured and prefixed, so a devtools filter on
 *    `[lms]` shows the full failure history of the session.
 *  - a capped in-memory buffer exposed as `window.__lmsClientErrors`, so a
 *    person on a support call can be told "open the console and run
 *    `copy(window.__lmsClientErrors)`" without a telemetry backend existing
 *    yet. Wiring this buffer to OpenTelemetry is Wave-3 work (survey 3.3).
 *
 * Never log headers, request/response bodies or tokens here — only the
 * context string, the error message and the HTTP status.
 */

export interface ClientErrorEntry {
  /** ISO timestamp of the first occurrence. */
  time: string;
  /** Where it happened, e.g. `GET /api/courses` or `learn/Dashboard load`. */
  context: string;
  /** The error's own message, never a serialized error object. */
  message: string;
  /** HTTP status when the failure was an HTTP response. */
  status?: number;
  /** How many consecutive identical failures this entry stands for. */
  count: number;
}

declare global {
  interface Window {
    __lmsClientErrors?: ClientErrorEntry[];
  }
}

const MAX_ENTRIES = 50;

const buffer: ClientErrorEntry[] = [];

function describeError(error: unknown): { message: string; status?: number } {
  if (error && typeof error === 'object') {
    const maybe = error as { message?: unknown; response?: { status?: unknown } };
    const status = typeof maybe.response?.status === 'number' ? maybe.response.status : undefined;
    if (typeof maybe.message === 'string' && maybe.message) {
      return { message: maybe.message, status };
    }
    return { message: Object.prototype.toString.call(error), status };
  }
  return { message: String(error) };
}

/**
 * Record a client-side failure. Safe to call from anywhere, including render
 * paths and interceptors; it never throws.
 */
export function logClientError(context: string, error: unknown): void {
  const { message, status } = describeError(error);

  // Consecutive repeats of the same failure (a poll, a retry loop) collapse
  // into one entry with a count, so the buffer holds history, not noise.
  const last = buffer[buffer.length - 1];
  if (last && last.context === context && last.message === message && last.status === status) {
    last.count += 1;
  } else {
    buffer.push({ time: new Date().toISOString(), context, message, status, count: 1 });
    if (buffer.length > MAX_ENTRIES) buffer.shift();
  }

  // This module IS the console sink; everything else should call it instead of console.error.
  console.error(`[lms] ${context} failed${status ? ` (${status})` : ''}: ${message}`);

  if (typeof window !== 'undefined') {
    window.__lmsClientErrors = buffer;
  }
}

/** The recorded failures, oldest first. Read-only view for tests and support tooling. */
export function getClientErrors(): readonly ClientErrorEntry[] {
  return buffer;
}

/** Empty the buffer. Exists for tests; production code has no reason to forget errors. */
export function clearClientErrors(): void {
  buffer.length = 0;
  if (typeof window !== 'undefined') {
    window.__lmsClientErrors = buffer;
  }
}
